import clsx from "clsx";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import {
  MdAdd,
  MdEdit,
  MdMoreHoriz,
  MdOutlineAttachment,
  MdOutlineCompareArrows,
  MdOutlineCreditCard,
  MdOutlineQueryStats,
  MdOutlineSwapVert,
} from "react-icons/md";
import { ETransactionType } from "src/helpers/constants";
import { motion } from "framer-motion";
import { memo, useMemo } from "react";
import useMobile from "src/hooks/useMobile";
import { useTranslation } from "react-i18next";
import ShinyCard from "../BankCard/ShinyCard";
import { getTotalByTransactionType } from "src/helpers/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { chain } from "lodash";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrencies from "src/hooks/useCurrencies";
import useCurrentUser from "src/hooks/useCurrentUser";
import { ROUTES } from "src/helpers/routes";
import useModalsStore from "src/store/modalsStore";
import temporalStore from "src/store/temporalStore";
import useColors from "src/hooks/useColors";
import CurrencyBadge from "../CurrencyBadge/CurrencyBadge";
import useUiPreferences from "src/store/useUiPreferences";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind, IButton } from "../Button/Button.types";

interface IAccountCard {
  account: any;
  animationIndex: number;
  showButton?: boolean;
  redirectTo?: string;
  customButtons?: IButton[];
}

const AccountCard: React.FC<IAccountCard> = ({
  account,
  animationIndex,
  showButton = false,
  redirectTo = "",
  customButtons = [],
}) => {
  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { getAccountColors } = useColors();
  const { setShowMoneyTransferModal, setShowEditAccountModal } =
    useModalsStore();
  const { setPreselectedAccountForTransfer } = temporalStore();
  const { getCurrencyDataByCode } = useCurrencies();
  const { getAccountById } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { allActiveTransactions } = useAllTransactions();

  const defaultRedirectTo = chain(location.pathname)
    .split("/") // divide en partes
    .get(1, "") // obtiene el índice 1 o "" si no existe
    .thru((part) => (part ? `/${part}` : "")) // añade "/" si existe
    .value();

  const currencySymbol = useMemo(() => {
    return (
      getCurrencyDataByCode({
        code: getAccountById(account?.account)?.currency,
      })?.symbol_native || ""
    );
  }, []);

  const getAvailableMoney = useMemo(() => {
    const income = getTotalByTransactionType({
      type: ETransactionType.INCOME,
      transactions: allActiveTransactions,
      account: account?.account,
    });
    const expenses = getTotalByTransactionType({
      type: ETransactionType.EXPENSE,
      transactions: allActiveTransactions,
      account: account?.account,
    });

    return income - expenses;
  }, [allActiveTransactions]);

  const visibleCustomButtons = useMemo(() => {
    return customButtons.filter((button) => !button.hide);
  }, [customButtons]);

  return (
    <>
      <motion.div
        className="flex w-full group"
        {...(enableAnimations && {
          initial: {
            opacity: 0,
            x: animationIndex % 2 === 0 ? -100 : 100,
          },
          animate: { opacity: 1, x: 0 },
          transition: {
            duration: 0.2,
            ease: "easeOut",
            delay: animationIndex * 0.1,
          },
        })}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "p-0",
            "md:p-0",
            "rounded",
            "md:gap-4",
            "gap-2",
            "cursor-pointer",
            "bg-gray-50/50 dark:bg-gray-900/50",
            "p-2",
            "md:p-2",
            "border",
            "border-blue-50",
            "dark:border-gray-800",
          ])}
          style={{
            ...getAccountColors(account?.account).string,
          }}
          onClick={(e: any) => {
            e?.preventDefault?.();
            navigate(redirectTo || `${defaultRedirectTo}/${account?.account}`);
          }}
        >
          <div
            className={clsx([
              "flex",
              "flex-row",
              "gap-2",
              "items-center",
              "w-full",
            ])}
          >
            <div
              className={clsx([
                "rounded",
                "inline-flex",
                "items-center",
                "justify-center",
                "cursor-pointer",
                "relative",
                "!h-[40px]",
                "!w-[40px]",
                "!min-h-[40px]",
                "!min-w-[40px]",
                "md:!h-[50px]",
                "md:!w-[50px]",
                "md:!min-h-[50px]",
                "md:!min-w-[50px]",
              ])}
              title={t("* Cuenta inactiva")}
            >
              <ShinyCard>
                <div
                  className={clsx([
                    "h-full",
                    "w-full",
                    "items-center",
                    "justify-center",
                    "flex",
                  ])}
                >
                  <MdOutlineCreditCard
                    size={isMobile ? ICON_SIZES.MD : ICON_SIZES.LG}
                    className={clsx(["group-hover:animate-pulse"])}
                  />
                </div>
              </ShinyCard>
            </div>
            <div
              className={clsx([
                "flex",
                "flex-col",
                "truncate",
                "flex-grow",
                "group-hover:opacity-50",
              ])}
            >
              <div className={clsx(["text-xs", "truncate", "md:text-xs"])}>
                {`${account?.settings?.disabled ? "* " : ""}${account?.name}`}
              </div>
              <CurrencyBadge
                value={getAvailableMoney}
                currencySymbol={currencySymbol}
                kind="NONE"
              />
            </div>

            {(showButton || visibleCustomButtons?.length > 0) && (
              <ButtonGroup
                gap={0}
                buttons={
                  showButton
                    ? [
                        {
                          tooltip: t("common:tooltips.more_options"),
                          icon: <MdMoreHoriz size={ICON_SIZES.SM} />,
                          kind: EButtonKind.NORMAL,
                          options: [
                            {
                              text: t("misc:menu.new_transaction"),
                              icon: <MdAdd size={ICON_SIZES.SM} />,
                              onClick: (e: any) => {
                                e?.preventDefault?.();
                                navigate(
                                  ROUTES.TRANSACTION_DETAIL(account?.account),
                                );
                              },
                              kind: EButtonKind.NORMAL,
                            },
                            {
                              text: t("misc:menu.transactions"),
                              icon: <MdOutlineSwapVert size={ICON_SIZES.SM} />,
                              onClick: (e: any) => {
                                e?.preventDefault?.();
                                navigate(ROUTES.TRANSACTIONS(account?.account));
                              },
                              kind: EButtonKind.NORMAL,
                            },
                            {
                              text: t("misc:menu.stats"),
                              icon: (
                                <MdOutlineQueryStats size={ICON_SIZES.SM} />
                              ),
                              onClick: (e: any) => {
                                e?.preventDefault?.();
                                navigate(ROUTES.REPORTS(account?.account));
                              },
                              kind: EButtonKind.NORMAL,
                            },
                            {
                              text: t("misc:menu.files"),
                              icon: (
                                <MdOutlineAttachment size={ICON_SIZES.SM} />
                              ),
                              onClick: (e: any) => {
                                e?.preventDefault?.();
                                navigate(ROUTES.FILES(account?.account));
                              },
                              kind: EButtonKind.NORMAL,
                            },
                            {
                              text: t("common:menus.transfer_funds"),
                              icon: (
                                <MdOutlineCompareArrows size={ICON_SIZES.SM} />
                              ),
                              onClick: (e: any) => {
                                e?.preventDefault?.();
                                setPreselectedAccountForTransfer(
                                  account?.account,
                                );
                                setShowMoneyTransferModal(true);
                              },
                              kind: EButtonKind.NORMAL,
                            },
                            {
                              text: t("common:tooltips.edit"),
                              icon: <MdEdit size={ICON_SIZES.SM} />,
                              onClick: (e: any) => {
                                e?.preventDefault?.();
                                setShowEditAccountModal(true, {
                                  accountId: account?._id,
                                  mode: "edit",
                                  initialValues: {
                                    account: account?.account,
                                    name: account?.name,
                                    bank: account?.bank,
                                    currency: account?.currency,
                                    bgColor: account?.settings?.bg,
                                    textColor: account?.settings?.color,
                                    isDisabled: account?.settings?.disabled,
                                    number: account?.number,
                                    image: account?.settings?.picture || "",
                                  },
                                });
                              },
                              kind: EButtonKind.NORMAL,
                            },
                          ],
                        },
                      ]
                    : customButtons
                }
              />
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default memo(AccountCard);
